import classNames from "classnames";
import {
	ControlAttachmentContext,
	ControlAttachmentOptions,
	getAttachmentClassNames
} from "../../ControlAttachment";
import {
	EMPTY_ARRAY,
	generateTrackingId,
	isNotNilOrEmpty
} from "client/Misc/utils";
import { shallowComparison } from "client/Misc/compareUtils";
import { undoDepth } from "prosemirror-history";
import {
	Command,
	EditorState,
	Transaction
} from "prosemirror-state";
import {
	forwardRef,
	ReactElement,
	ReadonlyRefObject,
	Ref,
	RefObject,
	useCallback,
	useContext,
	useImperativeHandle,
	useMemo,
	useRef
} from "react";
import { RichTextEditorState, RichTextEditorStateKind } from "../types";
import styles from "./RichTextEditorContent.module.less";
import { ProsemirrorView, ProsemirrorViewHandle } from "../../ProsemirrorView/ProsemirrorView";
import { EditorView } from "prosemirror-view";
import {
	Mark,
	Schema
} from "prosemirror-model";
import { LinkMarkAttributes } from "../schema/marks/link";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { isEmptyContent } from "../htmlConversion";
import {
	MentionInputSuggestion,
	MentionInputSuggestionBoxHandle,
	MentionInputSuggestionBoxProps
} from "client/Components/MentionInput/MentionInput";
import {
	useSynchronizedRef,
	useValueEqualMemo
} from "client/Hooks";
import { useMentions } from "client/Components/MentionInput/useMentions";
import {
	insertMentionAsLink,
	insertMentionExplicitly,
	RichTextEditorMentionConfiguration
} from "../commands/insertMention";
import { Rect } from "client/Misc/commonTypes";
import { isSafari } from "client_legacy/ndaw/mobile";
import { hasMarks, hasNodes } from "../schema";
import { never } from "client_legacy/ndaw/errors";
import { columnResizingPluginKey } from "prosemirror-tables";
import { UniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import {
	SelectionAnchor,
	useSelectionAnchor
} from "./useSelectionAnchor";

export type { SelectionAnchor, SelectionDirection } from "./useSelectionAnchor";

export type RichTextEditorContentHandle = {
	hasFocus(): boolean;
	focus(): void;
	getUndoDepth(): number;
};

export type MentionRichTextEditorContentProps = {
	mentionTypes?: undefined;
	renderSuggestionBox?: undefined;
	onInsertSuggestion?: undefined;
} | {
	mentionTypes:
		| string
		| readonly string[]
		| Record<string, RichTextEditorMentionConfiguration>;
	renderSuggestionBox(
		props: MentionInputSuggestionBoxProps,
		ref: Ref<MentionInputSuggestionBoxHandle>,
		rect: Rect
	): ReactElement;
	onInsertSuggestion?(
		suggestion: MentionInputSuggestion,
		mentionType: string
	): void;
};

export type RichTextEditorContentProps = {
	value: RichTextEditorState;
	onChange(newState: RichTextEditorState, isSignificant: boolean): void;
	onBlur?(newState: RichTextEditorState): void;
	onFocus?(newState: RichTextEditorState): void;
	placeholder?: string | ReactElement;
	isInvalid?: boolean;
	isDisabled?: boolean;
	isBorderless?: boolean;
	hasNoPadding?: boolean;
	hasNoBackgroundColor?: boolean;
	height?: "auto" | "fill" | "resizable" | number;
	elementModifiers?: RichTextEditorElementModifiers;
	attachedOn?: ControlAttachmentOptions;
	onSelectionAnchorChanged?: (anchor: SelectionAnchor | null) => void;
	trackingId: string;
}
	& MentionRichTextEditorContentProps;

export const RichTextEditorContent = forwardRef(
	(props: RichTextEditorContentProps, forwardedRef: Ref<RichTextEditorContentHandle>): ReactElement => {
		const {
			value,
			onChange,
			placeholder,
			isDisabled = false,
			isInvalid = false,
			onBlur,
			onFocus,
			height,
			isBorderless = false,
			hasNoPadding = false,
			hasNoBackgroundColor = false,
			elementModifiers = {},
			attachedOn = EMPTY_ARRAY,
			onSelectionAnchorChanged,
			trackingId,
			mentionTypes: mentionTypeConfigurationsFromProps = EMPTY_ARRAY,
			onInsertSuggestion,
			renderSuggestionBox
		} = props;

		if (value.kind !== RichTextEditorStateKind.ProseMirror) {
			throw new Error("This component only works with prosemirror state.");
		}

		const mentionTypesUnstable = useMemo(
			(): readonly string[] => {
				return typeof mentionTypeConfigurationsFromProps === "string"
					? [mentionTypeConfigurationsFromProps]
					: Array.isArray(mentionTypeConfigurationsFromProps)
						? mentionTypeConfigurationsFromProps
						: Object.keys(mentionTypeConfigurationsFromProps);
			},
			[mentionTypeConfigurationsFromProps]
		);

		const mentionTypes = useValueEqualMemo(
			(): readonly string[] => mentionTypesUnstable,
			[mentionTypesUnstable]
		);

		const {
			attachedOn: attachmentsFromContext
		} = useContext(ControlAttachmentContext);

		const {
			id
		} = useContext(UniqueInputIdContext);

		const viewRef: RefObject<ProsemirrorViewHandle | null> = useRef<ProsemirrorViewHandle>(null);

		const {
			editorState
		} = value;

		const mentionTypeConfigurationsRef: ReadonlyRefObject<
			| string
			| readonly string[]
			| Record<string, RichTextEditorMentionConfiguration>
		> = useSynchronizedRef(mentionTypeConfigurationsFromProps);

		const getMentionConfiguration = useCallback(
			(type: string): RichTextEditorMentionConfiguration => {
				const mentionTypeConfigurations: string | readonly string[] | Record<string, RichTextEditorMentionConfiguration> = mentionTypeConfigurationsRef.current;

				if (typeof mentionTypeConfigurations === "string" || Array.isArray(mentionTypeConfigurations)) {
					return defaultMentionConfiguration;
				}

				const result: RichTextEditorMentionConfiguration | undefined = (mentionTypeConfigurations as Record<string, RichTextEditorMentionConfiguration>)[type];

				if (!result) {
					return defaultMentionConfiguration;
				}

				return result;
			},
			[mentionTypeConfigurationsRef]
		);

		const tryGetInsertSuggestionCommand = useCallback(
			(type: string): TryResult<
				((from: number, to: number, suggestion: MentionInputSuggestion) => Command)
			> => {
				const schema: Schema<never, never> = editorState.schema;

				const configuration: RichTextEditorMentionConfiguration = getMentionConfiguration(type);

				switch (configuration.kind) {
					case "explicit-mention":
						if (hasMarks(schema, ["mention"])) {
							return {
								wasSuccessful: true,
								result: (
									from: number,
									to: number,
									suggestion: MentionInputSuggestion
								): Command => insertMentionExplicitly(
									schema,
									from,
									to,
									type,
									suggestion
								)
							};
						} else {
							return {
								wasSuccessful: false
							};
						}
					case "insert-anchor":
						if (hasMarks(schema, ["link"])) {
							return {
								wasSuccessful: true,
								result: (
									from: number,
									to: number,
									suggestion: MentionInputSuggestion
								): Command => insertMentionAsLink(
									schema,
									from,
									to,
									type,
									suggestion,
									configuration.hrefPattern
								)
							};
						} else {
							return {
								wasSuccessful: false
							};
						}
					default:
						never(configuration);
				}
			},
			[getMentionConfiguration, editorState.schema]
		);

		const isMentionTypeSupportedBySchema = useCallback(
			(type: string): boolean => {
				return tryGetInsertSuggestionCommand(type).wasSuccessful;
			},
			[tryGetInsertSuggestionCommand]
		);

		const insertSuggestionHandler = useCallback(
			(
				from: number,
				to: number,
				type: string,
				suggestion: MentionInputSuggestion
			): Command | undefined => {
				const commandResolution: TryResult<(from: number, to: number, suggestion: MentionInputSuggestion) => Command> = tryGetInsertSuggestionCommand(type);

				if (!commandResolution.wasSuccessful) {
					return undefined;
				}

				return commandResolution.result(
					from,
					to,
					suggestion
				);
			},
			[tryGetInsertSuggestionCommand]
		);

		const renderSuggestionBoxHandler = useCallback(
			(
				props: MentionInputSuggestionBoxProps,
				ref: Ref<MentionInputSuggestionBoxHandle>,
				rect: Rect
			): ReactElement | null => {
				if (!renderSuggestionBox) {
					return null;
				}

				return renderSuggestionBox(
					props,
					ref,
					rect
				);
			},
			[renderSuggestionBox]
		);

		const {
			suggestionBox,
			textInputHandler,
			keyDownHandler
		} = useMentions({
			editorState,
			insertSuggestion: insertSuggestionHandler,
			mentionTypes,
			renderSuggestionBox: renderSuggestionBoxHandler,
			viewRef,
			onInsertSuggestion,
			isMentionTypeSupportedBySchema,
			trackingId: generateTrackingId(trackingId, "__suggestion-list")
		});

		const changeHandler = useCallback(
			(prosemirrorState: EditorState, isSignificant: boolean): void => {
				onChange(
					{
						kind: RichTextEditorStateKind.ProseMirror,
						editorState: prosemirrorState
					},
					isSignificant
				);
			},
			[onChange]
		);

		useImperativeHandle(
			forwardedRef,
			() => ({
				hasFocus(): boolean {
					return viewRef.current
						?.hasFocus()
							?? false;
				},
				focus(): void {
					viewRef.current
						?.focus();
				},
				getUndoDepth(): number {
					return undoDepth(editorState);
				}
			}),
			[editorState]
		);

		const {
			handleDOMEvents
		} = useSelectionAnchor({
			viewRef,
			editorState,
			onSelectionAnchorChanged
		});

		const blurHandler = useCallback(
			(): void => {
				onBlur?.(value);
			},
			[onBlur, value]
		);

		const focusHandler = useCallback(
			(): void => {
				onFocus?.(value);
			},
			[onFocus, value]
		);

		const clickHandler = useCallback(
			(view: EditorView, pos: number, event: MouseEvent): void => {
				if (!event.ctrlKey) {
					return;
				}

				const schema: Schema<never, never> = view.state
					.schema;

				if (!hasMarks(schema, ["link"])) {
					return;
				}

				const marksAtPos: readonly Mark[] = view.state
					.doc
					.resolve(pos)
					.marks();

				const linkMark: Mark | undefined = marksAtPos.find(
					m => m.type === schema.marks
						.link
				);

				if (!linkMark) {
					return;
				}

				const attrs = linkMark.attrs as LinkMarkAttributes;

				if (isNotNilOrEmpty(attrs.href)) {
					navigateTo(attrs.href, true);
				}
			},
			[]
		);

		const shouldRenderPlaceholder = useMemo(
			(): boolean => {
				return isEmptyContent(editorState);
			},
			[editorState]
		);

		const shouldApplySafariFix = useMemo(
			(): boolean => {
				return isSafari();
			},
			[]
		);

		const isSignificantChangeInContextOfSchema = useCallback(
			(tr: Transaction): boolean => {
				return isSignificantChange(tr, editorState.schema);
			},
			[editorState.schema]
		);

		return (
			<>
				<ProsemirrorView
					ref={viewRef}
					additionalClassName={classNames(
						styles.root,
						{
							[styles.root_paragraphNoMargin]: elementModifiers.paragraph
								?.hasNoMargin === true,
							[styles.root_imageNoMaxWidth]: elementModifiers.image
								?.hasNoMaxWidth === true,
							[styles.root_imageVerticalAlignBottom]: elementModifiers.image
								?.verticalAlignment === "bottom",
							[styles.root_applySafariFix]: shouldApplySafariFix
						},
						getAttachmentClassNames(attachedOn, attachmentsFromContext)
					)}
					trackingId={trackingId}
					attachedOn={attachedOn}
					hasNoBackgroundColor={hasNoBackgroundColor}
					hasNoPadding={hasNoPadding}
					height={height}
					isBorderless={isBorderless}
					isDisabled={isDisabled}
					isInvalid={isInvalid}
					placeholder={placeholder}
					onBlur={blurHandler}
					onFocus={focusHandler}
					editorState={editorState}
					onChange={changeHandler}
					handleClick={clickHandler}
					maxHeight={undefined}
					shouldRenderPlaceholder={shouldRenderPlaceholder}
					isSignificantChange={isSignificantChangeInContextOfSchema}
					handleTextInput={textInputHandler}
					handleKeyDown={keyDownHandler}
					handleDOMEvents={handleDOMEvents}
					id={id}
				/>
				{suggestionBox}
			</>
		);
	}
);
RichTextEditorContent.displayName = "RichTextEditorContent";

export type RichTextEditorImageVerticalAlignment =
	| "default"
	| "bottom";

export type RichTextEditorElementModifiers = {
	paragraph?: {
		hasNoMargin?: boolean;
	};
	image?: {
		hasNoMaxWidth?: boolean;
		verticalAlignment?: RichTextEditorImageVerticalAlignment;
	};
};

function isSignificantChange(tr: Transaction, schema: Schema<never, never>): boolean {
	if (hasNodes(schema, ["table", "table_cell"])
		&& isTableColumnResizeFinished(tr)
	) {
		return true;
	}

	return false;
}

function isTableColumnResizeFinished(tr: Transaction): boolean {
	const pluginMetadata: unknown = tr.getMeta(columnResizingPluginKey);

	// NOTE: This is the metadata set by the plugin when user releases the mouse and finishes resizing
	// Review this when updating prosemirror-tables package.
	return shallowComparison(pluginMetadata, { setDragging: null });
};

const defaultMentionConfiguration: RichTextEditorMentionConfiguration = { kind: "explicit-mention" };